import styled from "styled-components";
import { useEffect, useRef } from "react";
import ReactDOM from "react-dom";

export const Portal = ({ children, root }) => {
  const el = useRef(document.createElement("div"));

  useEffect(() => {
    if (!root) return;
    const currentEl = el.current;
    root.appendChild(el.current);

    return () => {
      root.removeChild(currentEl);
    };
  }, [root]);

  return ReactDOM.createPortal(children, el.current);
};

export const RootContainer = ({ className, children, id, ref }) => {
  return (
    <div ref={ref} className={className} id={id} key={id}>
      {children}
    </div>
  );
};

export const Container = styled(RootContainer)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const Pane = styled.div`
  display: flex;
  flex-direction: column;
  transition: all 0.5s ease-in;
  transform: rotate3d(0, 0, 0, 0deg);
`;

export const Header = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 10px;
  height: 30px;
`;

export const Tabs = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: clip;
  padding: 0px;

  &::-webkit-scrollbar {
    display: none;
    background-color: #111;
    width: 16px;
  }

  &::-webkit-scrollbar-track {
    background-color: #111;
  }

  &::-webkit-scrollbar-button {
    display: none;
  }

  & > div {
    margin-right: ${(props) => props.$spacing}px;
  }
`;

export const Spacer = styled.div`
  flex-grow: 4;
  margin-right: 5px;
`;

export const LeftBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-left: 5px;
`;

export const Extra = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-left: 5px;
`;

export const InputRadio = styled.input.attrs((props) => {
  const obj = {
    type: "radio",
    name: `tabs-${props.$group}`,
    id: `tab-${props.$id}`
  };
  obj.checked = props.$active ? true : false;

  return obj;
})`
  display: none;

  &:checked {
    & ~ .glider {
      transform: translateX(${(props) => props.$translate}px);
    }

    & + label {
      color: #fff !important;
      font-weight: 600;

      & > .notification {
        background-color: #185ee0;
        color: #fff;
      }
    }
  }
`;

export const Glider = styled.div.attrs((props) => ({}))`
  position: absolute;
  display: flex;
  width: ${(props) => props.$tabWidth ?? 10}px;
  height: 30px;
  border: 2px solid #12406c;
  //z-index: 1;
  background-color: transparent;
  border-radius: 8px;
  transition: 0.25s ease-out;
  //padding: 0 16px;
  color: #fff;

  justify-content: flex-end;
`;

export const Draggable = styled.div.attrs((props) => ({}))`
  position: relative;
  top: -2px;
  left: +2px;
  width: 10px;
  height: 10px;
  border-top: 2px solid #333;
  border-right: 2px solid #333;
  border-top-right-radius: 8px;
`;

export const Tab = styled.label.attrs((props) => ({
  htmlFor: `tab-${props.$id}`
}))`
  display: ${(props) => (props.hidden ? "none" : "flex")};
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.15s ease-in;
  user-select: none;

  height: 30px;
  overflow: visible;

  white-space: nowrap;
  text-overflow: ellipsis;

  color: #888;

  &.active {
    color: #fff;
  }

  &:not(.active) {
    //border: 0.5px solid #111;
  }

  &:hover:not(.active) {
    color: #ccc;
    background-color: #222;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 0px 10px !important;
  border: 2px solid #333;
  border-radius: 4px;
  font-size: 1rem;
  color: #333;
  height: 30px !important;

  &:focus {
    border-color: #13406c;
    border-width: 2px;
  }

  &:hover:not(:focus) {
    border-color: #444;
  }
`;

export const Button = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 14px;
  border: 2px solid #13406c;
  border-radius: 4px;
  font-size: 1rem;
  background-color: #111;
  color: #888;
  cursor: pointer;
  height: 27px;

  &:hover {
    color: #fff;
    background-color: #13406c;
    font-weight: 400;
  }
`;

export const HiddenButton = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 100%;
  background-color: #000;
  user-select: none;

  animation: fadeIn 1s;
`;

export const ArrowUp = styled.div`
  position: absolute;
  top: ${(props) => props.$top ?? "20"}px;
  left: ${(props) => (props.$left ? `${props.$left}px` : "unset")};
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #12406c;
  z-index: 1001;
`;

export const HiddenDropdown = styled.div`
  position: absolute;
  top: ${(props) => props.$top ?? "30"}px;
  left: ${(props) => (props.$left ? `${props.$left}px` : "unset")};
  min-width: 250px;
  //height: 300px;
  background-color: #111;
  border: 1px solid #12406c;
  border-radius: 8px;
  z-index: 1005;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: nowrap;
  transition: height 0.4s ease;
  user-select: none;
  overflow-y: scroll;
  filter: drop-shadow(2px 2px 4px #222);

  &::-webkit-scrollbar {
    display: none;
    background-color: #111;
    width: 16px;
  }

  &::-webkit-scrollbar-track {
    background-color: #111;
  }

  &::-webkit-scrollbar-button {
    display: none;
  }
`;

export const HiddenItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  width: 100%;
  user-select: none;
  z-index: 1005;

  cursor: pointer;
  font-size: 1rem;
  font-family: "Roboto", sans-serif;
  font-weight: 100;
  padding: 6px 15px;
  border-radius: 8px;

  &:hover {
    background-color: #222;
  }
`;

export const HiddenItemShortcut = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  border-radius: 4px;
  user-select: none;
  font-size: 12px;
  color: #888;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 8px;
`;
export const HiddenItemIcon = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  user-select: none;
  height: 30px;

  padding: 4px;
  &::after {
    content: "...";
    color: #888;
    font-size: 1.2rem;
    font-weight: 600;
  }
`;

export const TabLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 1rem;
`;

export const TabIcon = styled.div`
  margin-right: 10px;
  color: ${(props) => props.$color};
`;

export const TabExtra = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-left: 10px;
`;

export const TabTitle = styled.div`
  font-size: 1rem;
  font-weight: 500;
  max-width: ${(props) => props.$width};
  color: ${(props) => (props.$isActive ? props.$activeColor : props.$color)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TabEdit = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 1rem;
  z-index: 4;
  min-width: 250px;

  & > * {
    margin-right: 5px;
  }
`;

export const TabDeleteIcon = styled.div`
  margin-left: 15px;
  margin-right: -5px;
  cursor: pointer;
  color: #444;

  &:hover {
    color: #f00;
  }
`;

export const AddPan = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: darkgreen;
  padding: 0 5px;
  border-radius: 2px;
  height: 15px;
  width: 15px;
  margin-right: 10px;

  &:hover {
    background-color: green;
  }

  &::after {
    content: "\\002B";
    font-weight: 400;
    font-size: 1rem;
    color: #000;
    cursor: pointer;
  }
`;

export const AddTab = styled.div`
  cursor: pointer;
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-left: 10px;
`;

export const TabStringTitle = styled.div`
  display: flex;

  align-items: center;
  justify-content: flex-start;
  font-size: 1em;
  // max-width: 200px;
  white-space: nowrap;

  overflow: hidden;
  height: 30px;

  text-overflow: ellipsis;
`;

// TAB MENU
// https://tablericons.com

const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  animation: fadeIn 0.5s;

  &:hover {
    color: #f0f;
  }
`;

export const ChevronDown = ({
  width = 24,
  height = 24,
  strokeWidth = 2,
  color = "currentColor",
  onClick = () => true
}) => {
  return (
    <ChevronContainer>
      <svg
        onClick={onClick}
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6l6 -6" />
      </svg>
    </ChevronContainer>
  );
};

export const ChevronUp = ({
  width = 24,
  height = 24,
  strokeWidth = 2,
  color = "currentColor",
  onClick = () => true
}) => {
  return (
    <ChevronContainer>
      <svg
        onClick={onClick}
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 15l6 -6l6 6" />
      </svg>
    </ChevronContainer>
  );
};

export const TabMenuBox = styled.div`
  position: relative;
  display: flex;
  height: 24px;
  width: 24px;
  user-select: none;
  margin-left: 5px;
  margin-right: -5px;
  ${(props) =>
    props.$isActive
      ? ""
      : `
    pointer-events: none;
    opacity: 0.3;
    cursor: not-allowed;
    `}

  animation: fadeIn 1s;

  // & > ${ArrowUp} {
  //   margin-left: 2px;
  // }
`;
